import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import SEO from '../components/seo/SEO'
import Button from '../components/ui/Button'
import { useAuth } from '../contexts/AuthContext'
import { useLocalePath } from '../hooks/useLocalePath'
import styles from './Auth.module.css'

export default function Auth() {
  const { t } = useTranslation('auth')
  const { t: tSeo } = useTranslation('seo')
  const { signIn, signUp, signInWithGoogle } = useAuth()
  const navigate = useNavigate()
  const lp = useLocalePath()
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    const result = isSignUp
      ? await signUp(email, password, name || undefined)
      : await signIn(email, password)
    setLoading(false)
    if (result.error) {
      setError(result.error)
    } else {
      navigate(lp('/dashboard'))
    }
  }

  const handleGoogle = async () => {
    setError(null)
    setLoading(true)
    const result = await signInWithGoogle()
    setLoading(false)
    if (result.error) {
      setError(result.error)
    } else {
      navigate(lp('/dashboard'))
    }
  }

  return (
    <div className={styles.page}>
      <SEO title={tSeo('auth.title')} description={tSeo('auth.description')} canonical="/auth" noindex />

      <section className={styles.section}>
        <motion.div
          className={styles.card}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className={styles.logoWrap}>
            <svg viewBox="0 0 44 44" width="36" height="36" fill="none">
              <circle cx="31.8" cy="8.1" r="2" fill="#F59E0B"/>
              <circle cx="27.6" cy="6.0" r="2" fill="#EFA51D"/>
              <circle cx="23.1" cy="5.0" r="2" fill="#E8AD30"/>
              <circle cx="18.5" cy="5.4" r="2" fill="#E2B443"/>
              <circle cx="14.2" cy="6.9" r="2" fill="#DBBC56"/>
              <circle cx="10.4" cy="9.6" r="2" fill="#D5C469"/>
              <circle cx="7.5" cy="13.2" r="2" fill="#CECC7C"/>
              <circle cx="5.6" cy="17.4" r="2" fill="#C8D48F"/>
              <circle cx="5.0" cy="22.0" r="2" fill="#C1DCA2"/>
              <circle cx="5.6" cy="26.6" r="2" fill="#BBE4B5"/>
              <circle cx="7.5" cy="30.8" r="2" fill="rgba(255,255,255,0.8)"/>
              <circle cx="10.4" cy="34.4" r="2" fill="rgba(255,255,255,0.8)"/>
              <circle cx="14.2" cy="37.1" r="2" fill="rgba(255,255,255,0.75)"/>
              <circle cx="18.5" cy="38.6" r="2" fill="rgba(255,255,255,0.7)"/>
              <circle cx="23.1" cy="39.0" r="2" fill="rgba(255,255,255,0.65)"/>
              <circle cx="27.6" cy="38.0" r="2" fill="rgba(255,255,255,0.6)"/>
              <circle cx="31.8" cy="35.9" r="2" fill="rgba(255,255,255,0.55)"/>
            </svg>
          </div>
          <h1>{isSignUp ? t('signUp.title') : t('signIn.title')}</h1>
          <p className={styles.subtitle}>{isSignUp ? t('signUp.subtitle') : t('signIn.subtitle')}</p>

          {error && <div className={styles.error}>{error}</div>}

          <form onSubmit={handleSubmit} className={styles.form}>
            {isSignUp && (
              <div className={styles.field}>
                <label htmlFor="name">{t('form.name')}</label>
                <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} placeholder={t('form.namePlaceholder')} />
              </div>
            )}
            <div className={styles.field}>
              <label htmlFor="email">{t('form.email')}</label>
              <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder={t('form.emailPlaceholder')} />
            </div>
            <div className={styles.field}>
              <label htmlFor="password">{t('form.password')}</label>
              <input type="password" id="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder={t('form.passwordPlaceholder')} minLength={6} />
            </div>
            <Button variant="primary" size="lg" type="submit" disabled={loading}>
              {loading ? t('form.loading') : isSignUp ? t('form.signUp') : t('form.signIn')}
            </Button>
          </form>

          <div className={styles.divider}><span>{t('form.or')}</span></div>

          <button className={styles.googleBtn} onClick={handleGoogle} disabled={loading}>
            <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {t('form.google')}
          </button>

          <p className={styles.switch}>
            {isSignUp ? t('form.hasAccount') : t('form.noAccount')}{' '}
            <button onClick={() => { setIsSignUp(!isSignUp); setError(null) }} className={styles.switchBtn}>
              {isSignUp ? t('form.signInLink') : t('form.signUpLink')}
            </button>
          </p>
        </motion.div>
      </section>
    </div>
  )
}
